<template>
  <!-- 廣告橫幅 -->
  <v-sheet v-resize="detectSliderWidth" class="banners__swiper" elevation="0" rounded>
    <div
      v-if="showHeader"
      class="swiper__header d-flex justify-space-between align-center"
      :class="{ 'wd-py-3': !isCategoryUiType, 'wd-mb-2': isCategoryUiType && !isMobile }"
    >
      <h2 v-if="!isCategoryUiType" class="header__title">{{ title }}</h2>
      <h1 v-else-if="!isMobile" class="wd-font-medium wd-text-otemplg wd-flex wd-items-center" v-html="title" />

      <BannerSwiperBtnGroup
        v-if="!isMobile && bannerItems.length > 1"
        :disabled-prev-btn="disabledPrevBtn"
        :disabled-next-btn="disabledNextBtn"
        @prev="prevSwiper"
        @next="nextSwiper"
      />
    </div>

    <div
      v-show="swiper"
      class="swiper__container"
      :class="swiperContainerClass"
      :style="isSingleSlidePerView && bannerItems.length > 1 ? { 'margin-left': `-${offsetWidth}px`, 'margin-right': `-${offsetWidth}px` } : ''"
    >
      <swiper-vue ref="relatedBannersSwiper" v-bind="swiperOption" @swiper="initSwiper" @click="handleSwiperClick">
        <swiper-slide v-for="(banner, index) in bannerItems" :key="`${banner.id}_${index}`" :style="{ width: `${slideWidth}px` }">
          <BannerSlide
            :is-mobile="isMobile"
            :banner="banner"
            :slide-width="slideWidth"
            :is-single-slide-per-view="isSingleSlidePerView"
            :is-category-ui-type="isCategoryUiType"
            :is-image-size-large="isImageSizeLarge"
          />
        </swiper-slide>
      </swiper-vue>
    </div>

    <BannerSwiperSkeleton
      v-if="!swiper"
      :class="swiperContainerClass"
      :slide-width="slideWidth"
      :is-mobile="isMobile"
      :is-single-slide-per-view="isSingleSlidePerView"
      :is-image-size-large="isImageSizeLarge"
      :is-category-ui-type="isCategoryUiType"
      :has-banner-description="hasBannerDescription"
    />
  </v-sheet>
</template>

<script setup lang="ts">
import type { Swiper as SwiperType } from 'swiper'
// Please do rename the Swiper to avoid duplicate naming with `swiper`, the return of useSwiperFactory
import { Swiper as SwiperVue, SwiperSlide } from 'swiper/vue'
import { BannerUiType, BannerActionType } from '@/view-models/common'
import { useUserInfo } from '@/composition/useMember'
import { useGtm } from '@/composition/useGtm'
import { GtmEventMap } from '@/view-models/gtm'
import type { Banner } from '@/view-models/common'
import { LEGACY_LG_BREAKPOINT, FUNNOW_BREAKPOINTS } from '@/constants/theme'

import BannerSlide from '@/components/swiper/BannerSlide.vue'
import BannerSwiperBtnGroup from '@/components/swiper/BannerSwiperBtnGroup.vue'
import BannerSwiperSkeleton from '@/components/swiper/BannerSwiperSkeleton.vue'
import PromoCampaignModal from '@/components/dialog/PromoCampaignModal.vue'
import { useGlobalStore } from '@/store-pinia/global'

interface Props {
  isMobile?: boolean
  title?: string
  items?: Banner[]
  uiType?: string
  isSingleSlidePerView?: boolean
  isImageSizeLarge?: boolean
  showHeader?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  isMobile: false,
  title: '',
  items: () => [],
  uiType: 'banner',
  isSingleSlidePerView: false,
  isImageSizeLarge: false,
  showHeader: true,
})

const { swiper, disabledPrevBtn, disabledNextBtn, initSwiper, prevSwiper, nextSwiper } = useSwiperFactory()

const globalStore = useGlobalStore()
const { sendGtmEvent } = useGtm()
const { userInfo } = useUserInfo()

const slideWidth = ref<number>(360)
const offsetWidth = ref<number>(0)
const autoplayInterval = ref<ReturnType<typeof setInterval> | null>(null)

const isCategoryUiType = computed(() => props.uiType === BannerUiType.CATEGORY)

const swiperOption = computed(() => ({
  allowTouchMove: true,
  setWrapperSize: true,
  spaceBetween: 8,
  speed: 300,
  slidesPerGroup: 1,
  loop: bannerItems.value.length > 1,
  slidesPerView: 'auto' as const,
  centeredSlides: props.isSingleSlidePerView && (bannerItems.value.length > 1 || props.isMobile),
  breakpoints: {
    // when window width is >= xs. xs is the same as isMobile determination
    [FUNNOW_BREAKPOINTS.xs]: {
      spaceBetween: 20,
    },
  },
}))

// 2 and 3 items double for swiper loop
const bannerItems = computed(() => (props.items.length === 2 || props.items.length === 3 ? props.items.concat(props.items) : props.items))
const hasBannerDescription = computed(() => bannerItems.value.some(banner => banner.description))
const swiperContainerClass = computed(() => (isCategoryUiType.value ? '' : 'wd-pt-1 wd-pb-3 wd-px-0'))

onMounted(() => {
  detectSliderWidth()

  autoplayInterval.value = setInterval(() => {
    swiper.value?.slideNext()
  }, 5000)
})

onUnmounted(() => {
  if (autoplayInterval.value) {
    clearInterval(autoplayInterval.value)
  }
})

watch(
  () => props.items,
  () => {
    switchSwiperBtnStatus()
  }
)

function handleSwiperClick(swiperInstance: SwiperType) {
  if (props.items.length <= 0) {
    return
  }
  const clickedIndex = swiperInstance.clickedIndex % bannerItems.value.length
  const clickedBanner = bannerItems.value[clickedIndex] || {}

  const isLogin = !!userInfo.value
  const event = {
    event: GtmEventMap.HOME_BANNER_CLICKED,
    payload: {
      'Banner ID': clickedBanner.id,
      'Banner Title': clickedBanner.title,
      Path: clickedBanner.link_target,
      Sequence: clickedIndex,
    },
  }
  sendGtmEvent(event, false)
  if (isLogin) {
    sendGtmEvent(event, true)
  }

  const linkType = clickedBanner.link_type
  const linkTarget = clickedBanner.link_target || ''

  if (linkType === BannerActionType.FromCurrentPage) {
    const match = linkTarget.match(/\/campaign\/(\d+)/)
    const promoCampaignId = match ? match[1] : null

    if (promoCampaignId) {
      globalStore.setRootDynamicComponentOption({ promoCampaignId })
      globalStore.setRootDynamicComponent(PromoCampaignModal)
      globalStore.setShouldRootDynamicComponentShow(true)
    }
  }
}

function detectSliderWidth() {
  nextTick(() => {
    onResize()

    if (!props.isSingleSlidePerView && props.isMobile) {
      slideWidth.value = 320
      return
    }
    if (!props.isSingleSlidePerView && !props.isMobile) {
      slideWidth.value = props.isImageSizeLarge ? 534 : 360
      return
    }

    if (window.innerWidth <= LEGACY_LG_BREAKPOINT) {
      offsetWidth.value = props.isMobile ? 0 : 20
      slideWidth.value = window.innerWidth - 40
    } else {
      const oneSideMargin = (window.innerWidth - 1440) / 2
      const offset = 96 + Math.round(Math.max(oneSideMargin, 0))
      offsetWidth.value = offset
      slideWidth.value = window.innerWidth - offset * 2
    }
  })
}

function switchSwiperBtnStatus() {
  if (!props.isMobile) {
    const totalSliders = props.items.length
    if (totalSliders > 1) {
      disabledPrevBtn.value = false
      disabledNextBtn.value = false
    }
  }
}

function onResize() {
  swiper.value?.update()
  switchSwiperBtnStatus()
}

defineExpose({ prevSwiper, nextSwiper, disabledPrevBtn, disabledNextBtn, swiper })
</script>

<style scoped lang="scss">
.banners__swiper {
  padding: 0;
  color: $black50;

  .swiper__header {
    .header__title {
      margin-bottom: 0;
      font-size: 17px;
      font-weight: 400;
      line-height: 24px;
    }
  }

  :deep(.v-skeleton-loader.v-skeleton-loader--is-loading) {
    .v-skeleton-loader__image {
      width: 360px;
      height: 135px;
      margin-bottom: 12px;
    }
    .v-skeleton-loader__card-heading {
      height: 44px;
    }
  }
  .mobile {
    :deep(.v-skeleton-loader.v-skeleton-loader--is-loading) {
      .v-skeleton-loader__image {
        width: 320px;
        height: 120px;
      }
    }
  }
}
</style>
